import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { toastr } from "react-redux-toastr";
import { authorizeUser } from "actions/account";
import { removeCookie } from "utils/cookies";
import { client, apiFormatter } from "utils/api";
import { PageLoader } from "components";
import PendingRemarkGate from "components/PendingRemarkGate";
import ProtectedView from "./ProtectedView";
import AppLogin from "./AppLogin";

const fetchPendingApprovalRemarks = async () => {
  const { data } = await client.get(
    "/remarks/pending_approval?include=reporter,reported_user"
  );

  return apiFormatter(data);
};

const App = ({ current_user, authorizeUser }) => {
  const [pending_remarks, setPendingRemarks] = useState([]);
  const [is_initial_loading, setInitialLoading] = useState(true);
  const [is_submitting, setSubmitting] = useState(false);

  const loadPendingRemarks = useCallback(
    async ({ show_loader = false } = {}) => {
      if (current_user?.status !== "success") {
        return;
      }

      if (show_loader) {
        setInitialLoading(true);
      }

      try {
        const remarks = await fetchPendingApprovalRemarks();
        setPendingRemarks(Array.isArray(remarks) ? remarks : []);
      } catch {
        setPendingRemarks([]);
      } finally {
        if (show_loader) {
          setInitialLoading(false);
        }
      }
    },
    [current_user?.status]
  );

  useEffect(() => {
    authorizeUser();
  }, [authorizeUser]);

  useEffect(() => {
    if (current_user?.status === "success") {
      loadPendingRemarks({ show_loader: true });
    }
  }, [current_user?.status, loadPendingRemarks]);

  const handleApproveRemark = async () => {
    const current_remark = pending_remarks[0];

    if (!current_remark || is_submitting) {
      return;
    }

    setSubmitting(true);

    try {
      await client.put(
        `/remarks/${current_remark.id}?include=reporter,reported_user`,
        { approved_at: new Date().toISOString() }
      );
      toastr.success("Sukces", "Uwaga została potwierdzona");
      await loadPendingRemarks();
    } catch {
      toastr.error(
        "Błąd",
        "Wystąpił błąd podczas potwierdzania uwagi"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (
    current_user?.status === "invalid" ||
    current_user?.status === "loading"
  ) {
    return <PageLoader />;
  }

  if (current_user?.status === "failure") {
    removeCookie("token");
    return <AppLogin />;
  }

  if (is_initial_loading) {
    return <PageLoader />;
  }

  const current_remark = pending_remarks[0] || null;

  if (current_remark) {
    return (
      <PendingRemarkGate
        remark={current_remark}
        pending_count={pending_remarks.length}
        is_submitting={is_submitting}
        onApprove={handleApproveRemark}
      />
    );
  }

  return <ProtectedView />;
};

export default connect(
  ({ current_user }) => ({ current_user }),
  (dispatch) => ({
    authorizeUser: () => dispatch(authorizeUser()),
  })
)(App);
